import { api } from "@breakout/backend/convex/_generated/api"
import type { Maybe } from "@breakout/shared/types/market"
import { convexQuery } from "@convex-dev/react-query"
import { usePrivy } from "@privy-io/react-auth"
import { useQuery } from "@tanstack/react-query"
import { useConvexAuth } from "convex/react"
import type { FunctionReturnType } from "convex/server"
import { useMemo } from "react"

export type CurrentUser = NonNullable<
	FunctionReturnType<typeof api.domains.user.auth.query.current>
>
export type Wallet = CurrentUser["wallets"][number]

export interface AuthState {
	isLoading: boolean
	isAuthenticated: boolean
	user: Maybe<CurrentUser>
	evmWallet: Wallet | null
	solWallet: Wallet | null
}

export function useAuthState(): AuthState {
	const { ready: privyReady } = usePrivy()
	const { isLoading: isConvexAuthLoading, isAuthenticated: isConvexAuthenticated } = useConvexAuth()

	// Only query user when authenticated
	const { data: user } = useQuery({
		...convexQuery(api.domains.user.auth.query.current, {}),
		enabled: isConvexAuthenticated,
	})

	return useMemo(() => {
		// Still loading if Convex auth is loading OR user query hasn't resolved yet
		const isLoading =
			!privyReady || isConvexAuthLoading || (isConvexAuthenticated && user === undefined)

		// Authenticated when Convex says so AND user exists in our DB
		const isAuthenticated = isConvexAuthenticated && user != null

		// Get primary wallets (prefer external over embedded for better UX with MetaMask etc.)
		let evmWallet: Wallet | null = null
		let solWallet: Wallet | null = null
		if (user?.wallets) {
			const evmWallets = user.wallets.filter(w => w.chainType === "ethereum")
			evmWallet = evmWallets.find(w => !w.isEmbedded) ?? evmWallets[0] ?? null

			const solWallets = user.wallets.filter(w => w.chainType === "solana")
			solWallet = solWallets.find(w => !w.isEmbedded) ?? solWallets[0] ?? null
		}

		return {
			isLoading,
			isAuthenticated,
			user: isConvexAuthenticated ? user : null,
			evmWallet,
			solWallet,
		}
	}, [isConvexAuthLoading, isConvexAuthenticated, privyReady, user])
}
